import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { Copy, UserPlus, Pencil, Ban, CheckCircle2, Send, XCircle, Mail, Trash2, KeyRound } from "lucide-react";
import { api, formatApiErrorDetail } from "@/lib/api";
import { Panel, StatusPill, inputCls, fmtMoney } from "@/components/Peep";

const EMPTY = { display_name: "", handle: "", email: "", bio: "", is_test: false };

function TestVisibility({ c, onChanged }) {
  const [names, setNames] = useState((c.test_viewers || []).join(", "));
  const save = async (is_test) => {
    try {
      await api.put(`/admin/creators/${c.id}/test-visibility`, { is_test, viewer_usernames: names.split(",").map((s) => s.trim()).filter(Boolean) });
      toast.success(is_test ? "Hidden test creator saved" : "Creator is now public");
      onChanged();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    }
  };
  if (!c.is_test) return <button onClick={() => window.prompt(`Type @${c.handle} to hide ${c.display_name} from the public as a test creator.`)?.trim().replace(/^@/, "").toLowerCase() === c.handle && save(true)} data-testid={`creator-make-test-${c.handle}`} className="mt-3 rounded-lg border border-amber-400/30 px-2 py-1.5 text-xs text-amber-200">Make hidden test creator</button>;
  return (
    <div data-testid={`creator-test-panel-${c.handle}`} className="mt-3 flex flex-wrap items-center gap-2 rounded-xl border border-amber-400/30 bg-amber-500/10 p-3 text-xs text-amber-100">
      <span className="font-semibold">Hidden test creator</span> · visible only to Super Admin, the creator and these subscribers:
      <input className={`${inputCls} min-w-[12rem] flex-1 py-1.5`} data-testid={`creator-test-viewers-${c.handle}`} placeholder="usernames, comma separated" value={names} onChange={(e) => setNames(e.target.value)} />
      <button onClick={() => save(true)} data-testid={`creator-test-save-${c.handle}`} className="rounded-lg border border-amber-300/40 px-2 py-1.5">Save viewers</button>
      <button onClick={() => window.confirm(`Make ${c.display_name} visible to everyone?`) && save(false)} data-testid={`creator-test-public-${c.handle}`} className="rounded-lg border border-white/20 px-2 py-1.5">Make public</button>
    </div>
  );
}

function InviteLink({ result }) {
  const full = `${window.location.origin}${result.invite_url}`;
  const copy = () => navigator.clipboard?.writeText(full).then(() => toast.success("Invite link copied"));
  return (
    <div data-testid="invite-link-box" className="mt-3 rounded-xl border border-[#d42be8]/40 bg-[#d42be8]/10 p-3">
      <p data-testid="invite-email-state" className="flex items-center gap-1.5 text-xs font-semibold text-white"><Mail size={13} />
        {result.email_sent ? "Branded invitation email sent." : `Email not sent${result.email_error ? ` — ${result.email_error}` : ""}`}
      </p>
      <p className="mt-1 text-xs text-[#f0a8fa]">Backup: share this one-time link privately (expires in {result.invite_expires_hours}h, shown once):</p>
      <div className="mt-2 flex items-center gap-2">
        <input readOnly value={full} data-testid="invite-link-input" className={`${inputCls} font-mono text-xs`} onFocus={(e) => e.target.select()} />
        <button onClick={copy} data-testid="invite-copy-btn" className="rounded-xl border border-white/15 p-2.5 hover:border-white/40"><Copy size={16} /></button>
      </div>
    </div>
  );
}

function DeleteBox({ c, onDone, onCancel }) {
  const [text, setText] = useState("");
  const [busy, setBusy] = useState(false);
  const run = async () => {
    setBusy(true);
    try {
      const { data } = await api.delete(`/admin/creators/${c.id}`, { data: { confirm_handle: text } });
      toast.success(`Creator deleted — ${data.content_removed} posts and ${data.files_removed} files removed, ${data.financial_records_retained} financial records kept`);
      onDone();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
      setBusy(false);
    }
  };
  return (
    <div data-testid={`creator-delete-box-${c.handle}`} className="mt-4 rounded-xl border border-red-500/40 bg-red-500/10 p-3 text-xs text-red-100">
      <p className="font-semibold">Permanently delete {c.display_name}?</p>
      <p className="mt-1 text-red-200/80">Removes their login, profile, posts, photos, videos (Supabase + Cloudflare), live sessions, follows and invitations. Payments, earnings ledger and payout records are kept for financial and legal records. Blocked while members have an active paid membership or earnings are unpaid. This cannot be undone and frees their creator slot.</p>
      <input value={text} onChange={(e) => setText(e.target.value)} placeholder={`Type ${c.handle} to confirm`} autoCapitalize="none" data-testid={`creator-delete-confirm-${c.handle}`} className={`${inputCls} mt-2`} />
      <div className="mt-2 flex gap-2">
        <button onClick={onCancel} className="rounded-lg border border-white/15 px-3 py-2">Cancel</button>
        <button disabled={busy || text.trim().replace(/^@/, "").toLowerCase() !== c.handle} onClick={run} data-testid={`creator-delete-run-${c.handle}`} className="rounded-lg bg-red-500/80 px-3 py-2 font-semibold text-white disabled:opacity-40">{busy ? "Deleting…" : "Delete permanently"}</button>
      </div>
    </div>
  );
}

function CreatorRow({ c, onChanged }) {
  const [del, setDel] = useState(false);
  const [edit, setEdit] = useState(false);
  const [form, setForm] = useState({ display_name: c.display_name, handle: c.handle, bio: c.bio || "" });
  const [invite, setInvite] = useState(null);
  const inv = c.invite || {};
  const act = async (action) => {
    if (action === "suspend" && !window.confirm(`Suspend ${c.display_name}? They are signed out, their studio is locked, they disappear from discovery and any live broadcast ends. Nothing is deleted and you can reactivate them.`)) return;
    if (action === "revoke" && c.account && !window.confirm(`Revoke access for ${c.display_name}? Their login stops working and every active session ends immediately. Content and financial records are kept (hidden), and their creator slot is freed.`)) return;
    if (action === "revoke" && !c.account && !window.confirm(`Revoke the pending invitation for ${c.display_name}? The link will stop working.`)) return;
    try {
      const { data } = await api.post(`/admin/creators/${c.id}/${action}`);
      setInvite(data.invite_url ? data : null);
      toast.success({ resend: "Invitation re-sent", revoke: c.account ? "Access revoked — all sessions ended" : "Invitation revoked", suspend: "Creator suspended", activate: "Creator reactivated" }[action]);
      onChanged();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    }
  };
  const save = async () => {
    try {
      await api.put(`/admin/creators/${c.id}`, form);
      toast.success("Profile updated");
      setEdit(false);
      onChanged();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    }
  };
  return (
    <div data-testid={`creator-row-${c.handle}`} className="rounded-2xl border border-white/10 bg-white/[0.03] p-4">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <p className="text-lg font-bold">{c.display_name} <span className="text-sm font-normal text-[#a7a0af]">@{c.handle}</span>{c.is_test && <span data-testid={`creator-test-badge-${c.handle}`} className="ml-2 rounded-full border border-amber-400/40 px-2 py-0.5 text-[0.6rem] font-semibold uppercase text-amber-200">Test · hidden</span>}</p>
          <p className="text-xs text-[#a7a0af]">{c.account ? `${c.account.email} · @${c.account.username}` : `Invitation for ${c.invite_email}`}</p>
          <p className="mt-1 flex flex-wrap items-center gap-2 text-xs text-[#a7a0af]">Invitation: <StatusPill status={inv.status} testid={`creator-invite-status-${c.handle}`} />
            {inv.status === "pending" && inv.expires_at && <span>expires {new Date(inv.expires_at).toLocaleString()}</span>}
            {inv.status === "pending" && inv.email_sent === false && <span className="text-amber-300">email not delivered — use Resend or copy link</span>}
          </p>
          <p className="mt-1 text-xs text-[#a7a0af]">Plan: {c.plan?.name} · {fmtMoney(c.plan?.price)} · {c.analytics.active_direct} direct members · {c.analytics.published_items} published</p>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <StatusPill status={c.status} testid={`creator-status-${c.handle}`} />
          <button onClick={() => setEdit(!edit)} data-testid={`creator-edit-${c.handle}`} className="rounded-lg border border-white/15 p-2 hover:border-white/40"><Pencil size={14} /></button>
          {!["suspended", "access_revoked", "revoked"].includes(c.status) && <button onClick={() => act("suspend")} data-testid={`creator-suspend-${c.handle}`} className="flex items-center gap-1 rounded-lg border border-red-400/30 px-2 py-1.5 text-xs text-red-300"><Ban size={13} /> Suspend</button>}
          {["suspended", "access_revoked"].includes(c.status) && c.account && <button onClick={() => act("activate")} data-testid={`creator-activate-${c.handle}`} className="flex items-center gap-1 rounded-lg border border-emerald-400/30 px-2 py-1.5 text-xs text-emerald-300"><CheckCircle2 size={13} /> Reactivate</button>}
          {c.account && c.status !== "access_revoked" && <button onClick={() => act("revoke")} data-testid={`creator-revoke-access-${c.handle}`} className="flex items-center gap-1 rounded-lg border border-red-400/30 px-2 py-1.5 text-xs text-red-300"><KeyRound size={13} /> Revoke access</button>}
          <button onClick={() => setDel(!del)} data-testid={`creator-delete-${c.handle}`} className="flex items-center gap-1 rounded-lg border border-red-500/50 px-2 py-1.5 text-xs text-red-300"><Trash2 size={13} /> Delete</button>
          {!c.account && <button onClick={() => act("resend")} data-testid={`creator-reinvite-${c.handle}`} className="flex items-center gap-1 rounded-lg border border-[#6a00ff]/40 px-2 py-1.5 text-xs text-[#c9a6ff]"><Send size={13} /> Resend invite</button>}
          {!c.account && inv.status === "pending" && <button onClick={() => act("revoke")} data-testid={`creator-revoke-${c.handle}`} className="flex items-center gap-1 rounded-lg border border-red-400/30 px-2 py-1.5 text-xs text-red-300"><XCircle size={13} /> Revoke</button>}
        </div>
      </div>
      {del && <DeleteBox c={c} onCancel={() => setDel(false)} onDone={() => { setDel(false); onChanged(); }} />}
      <TestVisibility c={c} onChanged={onChanged} />
      {edit && (
        <div className="mt-4 grid gap-2 md:grid-cols-3">
          <input className={inputCls} data-testid={`creator-edit-name-${c.handle}`} value={form.display_name} onChange={(e) => setForm({ ...form, display_name: e.target.value })} placeholder="Display name (3–30 letters)" />
          <input className={inputCls} data-testid={`creator-edit-handle-${c.handle}`} value={form.handle} onChange={(e) => setForm({ ...form, handle: e.target.value })} placeholder="handle" autoCapitalize="none" />
          <input className={inputCls} value={form.bio} onChange={(e) => setForm({ ...form, bio: e.target.value })} placeholder="Short bio" />
          <button onClick={save} data-testid={`creator-edit-save-${c.handle}`} className="btn-brand rounded-xl py-2.5 text-sm font-semibold md:col-span-3">Save changes</button>
        </div>
      )}
      {invite && <InviteLink result={invite} />}
    </div>
  );
}

export default function AdminCreators() {
  const [rows, setRows] = useState([]);
  const [cap, setCap] = useState(null);
  const [form, setForm] = useState(EMPTY);
  const [created, setCreated] = useState(null);
  const [busy, setBusy] = useState(false);
  const load = useCallback(() => {
    api.get("/admin/creators").then((r) => { setRows(r.data.creators); setCap(r.data); }).catch(() => {});
  }, []);
  useEffect(() => { load(); }, [load]);

  const create = async (e) => {
    e.preventDefault();
    setBusy(true);
    setCreated(null);
    try {
      const { data } = await api.post("/admin/creators", { ...form, display_name: form.display_name.trim(), handle: form.handle.trim().replace(/^@/, "").toLowerCase(), email: form.email.trim() });
      setCreated(data);
      setForm(EMPTY);
      toast.success("Creator invited");
      load();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div data-testid="admin-creators" className="flex flex-col gap-6">
      <div>
        <h1 className="text-3xl font-extrabold">Creators</h1>
        <p className="mt-1 text-sm text-[#a7a0af]">Invite-only. Public creator registration is disabled.</p>
        {cap && <p data-testid="creator-capacity" className="mt-1 text-xs text-[#f0a8fa]">{cap.active_count} active · {cap.invited_count} invited · limit {cap.max_active_creators} creators</p>}
      </div>
      <Panel title="Invite a creator">
        <form onSubmit={create} noValidate className="grid gap-3 md:grid-cols-2">
          <label className="flex flex-col gap-1 text-xs text-[#a7a0af]">Display name (shown to subscribers)
            <input className={inputCls} data-testid="new-creator-name" placeholder="e.g. Sasha or Maria Lopez" maxLength={30} value={form.display_name} onChange={(e) => setForm({ ...form, display_name: e.target.value })} /></label>
          <label className="flex flex-col gap-1 text-xs text-[#a7a0af]">Handle (profile address, saved in lowercase)
            <div className="flex items-center"><span className="mr-1 text-sm text-[#6f6878]">@</span>
              <input className={inputCls} data-testid="new-creator-handle" placeholder="e.g. sasha" autoCapitalize="none" autoCorrect="off" spellCheck={false} maxLength={30} value={form.handle} onChange={(e) => setForm({ ...form, handle: e.target.value })} /></div></label>
          <label className="flex flex-col gap-1 text-xs text-[#a7a0af]">Creator's email
            <input type="email" className={inputCls} data-testid="new-creator-email" placeholder="name@example.com" autoCapitalize="none" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} /></label>
          <label className="flex flex-col gap-1 text-xs text-[#a7a0af]">Short bio (optional)
            <input className={inputCls} data-testid="new-creator-bio" placeholder="Optional" value={form.bio} onChange={(e) => setForm({ ...form, bio: e.target.value })} /></label>
          <label className="flex items-center gap-2 text-xs text-[#a7a0af] md:col-span-2">
            <input type="checkbox" data-testid="new-creator-is-test" checked={form.is_test} onChange={(e) => setForm({ ...form, is_test: e.target.checked })} />
            Test creator — hidden from the public (only Super Admin, the creator and chosen test subscribers can see it)
          </label>
          <button disabled={busy} data-testid="new-creator-submit" className="btn-brand flex items-center justify-center gap-2 rounded-xl py-3 text-sm font-semibold md:col-span-2"><UserPlus size={16} /> {busy ? "Sending invitation…" : "Create & email invitation"}</button>
        </form>
        {created && <InviteLink result={created} />}
      </Panel>
      <div className="flex flex-col gap-3">
        {rows.length === 0 && <p data-testid="creators-empty" className="text-sm text-[#a7a0af]">No creators yet. Each new creator starts with an empty studio and only ever sees their own content.</p>}
        {rows.map((c) => <CreatorRow key={c.id} c={c} onChanged={load} />)}
      </div>
    </div>
  );
}
